import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "fixed inset-0 z-[100] overflow-y-auto font-roboto"
};
const _hoisted_2 = { class: "flex h-full sm:min-h-full items-end sm:items-center justify-center p-0 sm:p-4" };
const _hoisted_3 = { class: "flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gray-50" };
const _hoisted_4 = { class: "text-lg sm:text-xl font-bold text-gray-900" };
const _hoisted_5 = { class: "flex-1 p-4 sm:p-6 overflow-y-auto" };
const _hoisted_6 = { class: "relative" };
const _hoisted_7 = {
    key: 0,
    class: "absolute right-3 top-3"
};
const _hoisted_8 = {
    key: 1,
    class: "absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-md"
};
const _hoisted_9 = { class: "flex flex-col items-center gap-2" };
const _hoisted_10 = {
    key: 2,
    class: "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
};
const _hoisted_11 = ["onClick"];
const _hoisted_12 = { class: "text-sm font-medium text-gray-900" };
const _hoisted_13 = { class: "text-xs text-gray-500" };
const _hoisted_14 = { class: "grid grid-cols-1 md:grid-cols-3 gap-4" };
const _hoisted_15 = { class: "md:col-span-2" };
const _hoisted_16 = {
    key: 0,
    class: "flex items-center justify-center py-4"
};
const _hoisted_17 = {
    key: 1,
    class: "rounded-lg border"
};
const _hoisted_18 = {
    key: 0,
    class: "bg-green-50 border-green-200 p-4 rounded-lg border"
};
const _hoisted_19 = { class: "flex items-start" };
const _hoisted_20 = { class: "flex-1" };
const _hoisted_21 = {
    key: 0,
    class: "mt-2 py-2 rounded text-sm"
};
const _hoisted_22 = { class: "text-gray-600" };
const _hoisted_23 = {
    key: 1,
    class: "mt-2 p-2 bg-green-100 rounded text-sm"
};
const _hoisted_24 = { class: "flex items-start" };
const _hoisted_25 = { class: "flex-1" };
const _hoisted_26 = { class: "bg-yellow-50 border-yellow-200" };
const _hoisted_27 = { class: "flex items-start" };
const _hoisted_28 = { class: "flex-1" };
const _hoisted_29 = { class: "text-yellow-700 text-sm mt-1" };
const _hoisted_30 = { class: "border-t border-gray-200 pt-4" };
const _hoisted_31 = {
    key: 0,
    class: "relative mt-3"
};
const _hoisted_32 = { class: "absolute bottom-2 right-3 text-xs text-gray-400" };
const _hoisted_33 = { class: "flex gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50" };
const _hoisted_34 = ["disabled"];
const _hoisted_35 = {
    key: 0,
    class: "inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
};
import { ref, computed, watch, nextTick, inject } from "vue";
import { useOrderPricing, useGooglePlaces } from "@shared";
import { MoneyUtils } from "@shared";
import { useTheme } from '@shared/composables/useTheme';
export default /*@__PURE__*/ _defineComponent({
    __name: 'DeliveryAddressModal',
    props: {
        isOpen: { type: Boolean },
        modelValue: {},
        additionalInfo: {}
    },
    emits: ["update:isOpen", "update:modelValue", "update:additionalInfo", "addressValidated", "saved"],
    setup(__props, { emit: __emit }) {
        const { getPlacePredictions, getPlaceDetails } = useGooglePlaces();
        const props = __props;
        const emit = __emit;
        // ✅ NEU: Zugriff auf Order Pricing für Lieferzonenvalidierung
        const orderPricing = useOrderPricing();
        // Get theme config
        const projectConfig = inject('projectConfig', null);
        const { theme } = useTheme();
        const fallbackPrimary = computed(() => theme.value.colors.primary || '#045da1');
        const fallbackDanger = computed(() => theme.value.colors.danger || fallbackPrimary.value);
        const modalButtonPrimary = computed(() => projectConfig?.branding?.modals?.buttons?.primaryBackground || projectConfig?.branding?.colors?.primary || fallbackPrimary.value);
        const modalInputFocusBorder = computed(() => projectConfig?.branding?.modals?.inputs?.focusBorderColor || projectConfig?.branding?.colors?.primary || fallbackPrimary.value);
        const modalInputFocusRing = computed(() => projectConfig?.branding?.modals?.inputs?.focusRingColor || `${modalInputFocusBorder.value}33`);
        const themeIconColor = computed(() => projectConfig?.branding?.colors?.primary || fallbackPrimary.value);
        const localData = ref({
            street: "",
            city: "",
            postalCode: "",
        });
        const additionalInfo = ref(props.additionalInfo || "");
        // ✅ Collapsible "Zusätzliche Informationen" State
        const additionalInfoOpen = ref(false);
        const additionalInfoTextarea = ref(null);
        // ✅ NEU: Google Places Autocomplete State
        const addressSuggestions = ref([]);
        const showSuggestions = ref(false);
        const isLoadingSuggestions = ref(false);
        const isLoadingPlaceDetails = ref(false); // ✅ Loading state für Place Details
        const suggestionsTimeout = ref(null);
        // ✅ NEU: Lieferzonenvalidierung State
        const isValidatingAddress = ref(false);
        const addressValidationResult = ref(null);
        const validationTimeout = ref(null);
        // Load data when modal opens
        watch(() => props.isOpen, (isOpen) => {
            if (isOpen) {
                localData.value = {
                    street: props.modelValue.street,
                    city: props.modelValue.city,
                    postalCode: props.modelValue.postalCode,
                };
                additionalInfo.value = props.additionalInfo || "";
                // Reset validation when opening
                addressValidationResult.value = null;
                addressSuggestions.value = []; // ✅ NEU: Reset suggestions
                showSuggestions.value = false;
            }
        });
        // Watch for additionalInfo prop changes
        watch(() => props.additionalInfo, (newValue) => {
            additionalInfo.value = newValue || "";
        });
        // Watch for additionalInfo changes and emit update
        watch(additionalInfo, (newValue) => {
            emit("update:additionalInfo", newValue);
        });
        const isFormValid = computed(() => {
            return (localData.value.street.trim().length > 0 &&
                localData.value.city.trim().length > 0 &&
                localData.value.postalCode.trim().length === 5 &&
                /^\d{5}$/.test(localData.value.postalCode));
        });
        // ✅ NEU: Erweiterte Speichern-Validierung
        const canSave = computed(() => {
            return isFormValid.value &&
                !isValidatingAddress.value &&
                (addressValidationResult.value?.success === false || // Fehler bei Validierung erlaubt Speichern
                    addressValidationResult.value?.inZone === true); // Oder erfolgreich in Zone
        });
        const outOfZoneStyle = computed(() => ({
            backgroundColor: `${fallbackDanger.value}12`,
            borderColor: `${fallbackDanger.value}55`
        }));
        function closeModal() {
            emit("update:isOpen", false);
        }
        // ✅ NEU: Debounced Adressvalidierung
        const debouncedValidateAddress = () => {
            if (validationTimeout.value) {
                clearTimeout(validationTimeout.value);
            }
            validationTimeout.value = setTimeout(() => {
                validateDeliveryAddress();
            }, 800); // 800ms delay nach letzter Eingabe
        };
        // ✅ NEU: Lieferzonenvalidierung
        const validateDeliveryAddress = async () => {
            if (!isFormValid.value) {
                addressValidationResult.value = null;
                return;
            }
            const fullAddress = `${localData.value.street}, ${localData.value.postalCode} ${localData.value.city}`;
            console.log('🔍 Validating delivery address:', fullAddress);
            isValidatingAddress.value = true;
            addressValidationResult.value = null;
            try {
                const result = await orderPricing.checkDeliveryZone(fullAddress);
                console.log('✅ Validation result:', result);
                addressValidationResult.value = result;
                // Emit validation result to parent
                emit("addressValidated", {
                    isValid: result.success,
                    inZone: result.inZone,
                    deliveryFeeCent: result.totalDeliveryFeeCent || result.deliveryFeeCent, // ✅ Verwende Gesamtgebühr
                    coordinates: result.coordinates
                });
            }
            catch (error) {
                console.error('❌ Address validation error:', error);
                addressValidationResult.value = {
                    success: false,
                    inZone: false,
                    error: 'Fehler bei der Adressprüfung'
                };
            }
            finally {
                isValidatingAddress.value = false;
            }
        };
        // ✅ NEU: Currency formatting helper
        const formatCurrency = (cents) => {
            return MoneyUtils.centToEuro(cents).toFixed(2) + ' €';
        };
        // ✅ NEU: Google Places Autocomplete Functions
        const loadAddressSuggestions = async (input) => {
            if (input.length < 3) {
                addressSuggestions.value = [];
                showSuggestions.value = false;
                return;
            }
            isLoadingSuggestions.value = true;
            try {
                console.log(`🔍 [Eckpunkt] Loading address suggestions for: ${input}`);
                const result = await getPlacePredictions(input);
                if (result.success && result.predictions) {
                    addressSuggestions.value = result.predictions;
                    showSuggestions.value = true;
                    console.log(`✅ [Eckpunkt] Loaded ${result.predictions.length} suggestions`);
                }
                else {
                    console.warn('❌ [Eckpunkt] Failed to load address suggestions:', result.error);
                    addressSuggestions.value = [];
                    showSuggestions.value = false;
                }
            }
            catch (error) {
                console.error('❌ [Eckpunkt] Error loading address suggestions:', error);
                addressSuggestions.value = [];
                showSuggestions.value = false;
            }
            finally {
                isLoadingSuggestions.value = false;
            }
        };
        // ✅ NEU: Debounced address search
        const debouncedLoadSuggestions = (input) => {
            if (suggestionsTimeout.value) {
                clearTimeout(suggestionsTimeout.value);
            }
            suggestionsTimeout.value = setTimeout(() => {
                loadAddressSuggestions(input);
            }, 300);
        };
        // ✅ NEU: Handle address input for autocomplete
        const onAddressInput = () => {
            const input = localData.value.street.trim();
            if (input.length >= 3) {
                debouncedLoadSuggestions(input);
            }
            else {
                addressSuggestions.value = [];
                showSuggestions.value = false;
            }
        };
        // ✅ NEU: Select address suggestion
        const selectAddressSuggestion = async (suggestion) => {
            console.log('🏠 [Eckpunkt] Selected address suggestion:', suggestion);
            showSuggestions.value = false;
            isLoadingPlaceDetails.value = true; // ✅ Start loading
            try {
                // Hole Details für die gewählte Adresse
                const result = await getPlaceDetails(suggestion.placeId);
                console.log('📍 [Eckpunkt] Place details result:', result);
                if (result.success && result.place) {
                    console.log('📍 [Eckpunkt] Place details data:', result.place);
                    // Extrahiere Adresskomponenten
                    const components = result.place.address_components || [];
                    let streetNumber = '';
                    let route = '';
                    let city = '';
                    let postalCode = '';
                    console.log('🔧 [Eckpunkt] Address components:', components);
                    // Parse Google Places Komponenten
                    for (const component of components) {
                        const types = component.types;
                        console.log('🔍 [Eckpunkt] Component:', component.long_name, 'Types:', types);
                        if (types.includes('street_number')) {
                            streetNumber = component.long_name;
                        }
                        else if (types.includes('route')) {
                            route = component.long_name;
                        }
                        else if (types.includes('locality')) {
                            // ✅ Only use 'locality' (actual city), NOT 'administrative_area_level_2' (district/region)
                            city = component.long_name;
                        }
                        else if (types.includes('postal_code')) {
                            postalCode = component.long_name;
                        }
                    }
                    // Kombiniere Straße und Hausnummer für das Straßenfeld
                    const fullStreet = `${route} ${streetNumber}`.trim();
                    console.log('🔧 [Eckpunkt] Parsed address parts:', {
                        street: fullStreet,
                        city: city.trim(),
                        postalCode: postalCode.trim(),
                        streetNumber,
                        route
                    });
                    // Automatisch die Felder ausfüllen
                    if (fullStreet)
                        localData.value.street = fullStreet;
                    if (city.trim())
                        localData.value.city = city.trim();
                    if (postalCode.trim())
                        localData.value.postalCode = postalCode.trim();
                    // ✅ NEU: Automatische Validierung nach Auswahl
                    await nextTick();
                    console.log('🔄 [Eckpunkt] Triggering automatic address validation...');
                    debouncedValidateAddress();
                }
                else {
                    console.warn('❌ [Eckpunkt] Failed to get place details:', result.error);
                    // Fallback: Verwende nur die Hauptadresse für das Straßenfeld
                    localData.value.street = suggestion.mainText || suggestion.description;
                    // ✅ NEU: Auch bei Fallback validieren
                    await nextTick();
                    debouncedValidateAddress();
                }
            }
            catch (error) {
                console.error('❌ [Eckpunkt] Error getting place details:', error);
                // Fallback: Verwende nur die Hauptadresse für das Straßenfeld
                localData.value.street = suggestion.mainText || suggestion.description;
                // ✅ NEU: Auch bei Fehlern validieren
                await nextTick();
                debouncedValidateAddress();
            }
            finally {
                isLoadingPlaceDetails.value = false; // ✅ Stop loading
            }
        };
        // ✅ Toggle Zusätzliche Informationen with auto-focus and scroll
        const toggleAdditionalInfo = () => {
            additionalInfoOpen.value = !additionalInfoOpen.value;
            if (additionalInfoOpen.value) {
                nextTick(() => {
                    additionalInfoTextarea.value?.focus();
                    additionalInfoTextarea.value?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                });
            }
        };
        function saveData() {
            if (!canSave.value)
                return;
            const updatedAddress = {
                street: localData.value.street,
                city: localData.value.city,
                postalCode: localData.value.postalCode,
            };
            emit("update:modelValue", updatedAddress);
            emit("update:additionalInfo", additionalInfo.value);
            emit("saved", updatedAddress); // ✅ NEW: Emit event when address is saved
            closeModal();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createCommentVNode(" Modal Overlay "),
                (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                    (_ctx.isOpen)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                            _createCommentVNode(" Backdrop "),
                            _cache[19] || (_cache[19] = _createElementVNode("div", { class: "fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300" }, null, -1 /* CACHED */)),
                            _createCommentVNode(" Modal "),
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("div", {
                                    class: "relative bg-white w-full h-full sm:h-auto sm:max-w-xl sm:rounded-xl shadow-xl transform transition-all duration-300 overflow-hidden flex flex-col",
                                    onClick: _cache[4] || (_cache[4] = _withModifiers(() => { }, ["stop"]))
                                }, [
                                    _createCommentVNode(" Modal Header "),
                                    _createElementVNode("div", _hoisted_3, [
                                        _createElementVNode("h2", _hoisted_4, [
                                            _createElementVNode("i", {
                                                class: "fas fa-map-marker-alt mr-2 sm:mr-3",
                                                style: _normalizeStyle({ color: themeIconColor.value })
                                            }, null, 4 /* STYLE */),
                                            _cache[5] || (_cache[5] = _createTextVNode(" Lieferadresse ", -1 /* CACHED */))
                                        ]),
                                        _createElementVNode("button", {
                                            onClick: closeModal,
                                            class: "p-2 hover:bg-gray-200 rounded-full transition-colors duration-200"
                                        }, [...(_cache[6] || (_cache[6] = [
                                                _createElementVNode("i", { class: "fas fa-times text-gray-600 text-lg sm:text-xl" }, null, -1 /* CACHED */)
                                            ]))])
                                    ]),
                                    _createCommentVNode(" Modal Content "),
                                    _createElementVNode("div", _hoisted_5, [
                                        _createElementVNode("form", {
                                            onSubmit: _withModifiers(saveData, ["prevent"]),
                                            class: "space-y-4",
                                            autocomplete: "on"
                                        }, [
                                            _createElementVNode("div", null, [
                                                _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-2" }, [
                                                    _createElementVNode("i", { class: "fas fa-road text-gray-400 mr-2" }),
                                                    _createTextVNode(" Straße und Hausnummer * ")
                                                ], -1 /* CACHED */)),
                                                _createElementVNode("div", _hoisted_6, [
                                                    _createCommentVNode(" ✅ NEU: Autocomplete Eingabefeld "),
                                                    _withDirectives(_createElementVNode("input", {
                                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((localData.value.street) = $event)),
                                                        name: "street",
                                                        type: "text",
                                                        autocomplete: "shipping street-address",
                                                        required: "",
                                                        class: "modal-input w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none transition-colors",
                                                        style: _normalizeStyle({
                                                            '--focus-border-color': modalInputFocusBorder.value,
                                                            '--focus-ring-color': modalInputFocusRing.value
                                                        }),
                                                        placeholder: "Beginnen Sie zu tippen für Adressvorschläge...",
                                                        onInput: onAddressInput,
                                                        onFocus: onAddressInput,
                                                        onBlur: debouncedValidateAddress
                                                    }, null, 36 /* STYLE, NEED_HYDRATION */), [
                                                        [_vModelText, localData.value.street]
                                                    ]),
                                                    _createCommentVNode(" ✅ Loading Indicator für Suggestions "),
                                                    (isLoadingSuggestions.value)
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[7] || (_cache[7] = [
                                                                _createElementVNode("div", { class: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" }, null, -1 /* CACHED */)
                                                            ]))]))
                                                        : _createCommentVNode("v-if", true),
                                                    _createCommentVNode(" ✅ Loading Overlay für Place Details "),
                                                    (isLoadingPlaceDetails.value)
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                                            _createElementVNode("div", _hoisted_9, [
                                                                _createElementVNode("div", {
                                                                    class: "animate-spin rounded-full h-6 w-6 border-b-2",
                                                                    style: _normalizeStyle({ borderBottomColor: fallbackPrimary.value })
                                                                }, null, 4 /* STYLE */),
                                                                _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-sm text-gray-600" }, "Lade Adresse...", -1 /* CACHED */))
                                                            ])
                                                        ]))
                                                        : _createCommentVNode("v-if", true),
                                                    _createCommentVNode(" ✅ NEU: Autocomplete Dropdown "),
                                                    (showSuggestions.value && addressSuggestions.value.length > 0)
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(addressSuggestions.value, (suggestion, index) => {
                                                                return (_openBlock(), _createElementBlock("div", {
                                                                    key: suggestion.placeId || index,
                                                                    onClick: ($event) => (selectAddressSuggestion(suggestion)),
                                                                    class: "px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                                }, [
                                                                    _createElementVNode("div", _hoisted_12, _toDisplayString(suggestion.mainText), 1 /* TEXT */),
                                                                    _createElementVNode("div", _hoisted_13, _toDisplayString(suggestion.secondaryText), 1 /* TEXT */)
                                                                ], 8 /* PROPS */, _hoisted_11));
                                                            }), 128 /* KEYED_FRAGMENT */))
                                                        ]))
                                                        : _createCommentVNode("v-if", true)
                                                ])
                                            ]),
                                            _createElementVNode("div", _hoisted_14, [
                                                _createElementVNode("div", null, [
                                                    _cache[10] || (_cache[10] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-2" }, [
                                                        _createElementVNode("i", { class: "fas fa-mail-bulk text-gray-400 mr-2" }),
                                                        _createTextVNode(" PLZ * ")
                                                    ], -1 /* CACHED */)),
                                                    _withDirectives(_createElementVNode("input", {
                                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((localData.value.postalCode) = $event)),
                                                        name: "postalCode",
                                                        type: "text",
                                                        autocomplete: "shipping postal-code",
                                                        required: "",
                                                        readonly: "",
                                                        maxlength: "5",
                                                        inputmode: "numeric",
                                                        pattern: "[0-9]{5}",
                                                        class: "w-full px-3 py-2 text-gray-500 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed",
                                                        placeholder: "12345"
                                                    }, null, 512 /* NEED_PATCH */), [
                                                        [_vModelText, localData.value.postalCode]
                                                    ])
                                                ]),
                                                _createElementVNode("div", _hoisted_15, [
                                                    _cache[11] || (_cache[11] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-2" }, [
                                                        _createElementVNode("i", { class: "fas fa-city text-gray-400 mr-2" }),
                                                        _createTextVNode(" Stadt * ")
                                                    ], -1 /* CACHED */)),
                                                    _withDirectives(_createElementVNode("input", {
                                                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((localData.value.city) = $event)),
                                                        name: "city",
                                                        type: "text",
                                                        autocomplete: "shipping address-level2",
                                                        required: "",
                                                        readonly: "",
                                                        class: "w-full px-3 py-2 text-gray-500 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed",
                                                        placeholder: "Musterstadt"
                                                    }, null, 512 /* NEED_PATCH */), [
                                                        [_vModelText, localData.value.city]
                                                    ])
                                                ])
                                            ]),
                                            _createCommentVNode(" ✅ NEU: Lieferzonenvalidierung "),
                                            (isValidatingAddress.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [...(_cache[12] || (_cache[12] = [
                                                        _createElementVNode("div", { class: "animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3" }, null, -1 /* CACHED */),
                                                        _createElementVNode("span", { class: "text-blue-600 font-medium" }, "Liefergebiet wird überprüft...", -1 /* CACHED */)
                                                    ]))]))
                                                : _createCommentVNode("v-if", true),
                                            _createCommentVNode(" ✅ NEU: Validierungsfeedback "),
                                            (addressValidationResult.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                                    _createCommentVNode(" Erfolg: In Lieferzone "),
                                                    (addressValidationResult.value.success && addressValidationResult.value.inZone)
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                                            _createElementVNode("div", _hoisted_19, [
                                                                _cache[15] || (_cache[15] = _createElementVNode("i", { class: "fas fa-check-circle text-green-600 mt-1 mr-3" }, null, -1 /* CACHED */)),
                                                                _createElementVNode("div", _hoisted_20, [
                                                                    _cache[14] || (_cache[14] = _createElementVNode("h4", { class: "font-semibold mt-1 text-green-800" }, "Lieferung möglich!", -1 /* CACHED */)),
                                                                    (addressValidationResult.value.totalDeliveryFeeCent && addressValidationResult.value.totalDeliveryFeeCent > 0)
                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                                                            _createElementVNode("strong", _hoisted_22, "Liefergebühren: " + _toDisplayString(formatCurrency(addressValidationResult.value.totalDeliveryFeeCent)), 1 /* TEXT */)
                                                                        ]))
                                                                        : (_openBlock(), _createElementBlock("div", _hoisted_23, [...(_cache[13] || (_cache[13] = [
                                                                                _createElementVNode("strong", { class: "text-gray-600" }, "Kostenlose Lieferung!", -1 /* CACHED */)
                                                                            ]))]))
                                                                ])
                                                            ])
                                                        ]))
                                                        : (addressValidationResult.value.success && !addressValidationResult.value.inZone)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                _createCommentVNode(" Fehler: Außerhalb Lieferzone "),
                                                                _createElementVNode("div", {
                                                                    class: "border",
                                                                    style: _normalizeStyle(outOfZoneStyle.value)
                                                                }, [
                                                                    _createElementVNode("div", _hoisted_24, [
                                                                        _createElementVNode("i", {
                                                                            class: "fas fa-exclamation-triangle mt-1 mr-3",
                                                                            style: _normalizeStyle({ color: fallbackDanger.value })
                                                                        }, null, 4 /* STYLE */),
                                                                        _createElementVNode("div", _hoisted_25, [
                                                                            _createElementVNode("h4", {
                                                                                class: "font-semibold",
                                                                                style: _normalizeStyle({ color: fallbackDanger.value })
                                                                            }, "Lieferung nicht möglich", 4 /* STYLE */),
                                                                            _createElementVNode("p", {
                                                                                class: "text-sm mt-1",
                                                                                style: _normalizeStyle({ color: fallbackDanger.value })
                                                                            }, " Diese Adresse liegt außerhalb unseres Liefergebiets. Bitte wählen Sie Abholung oder eine andere Adresse. ", 4 /* STYLE */)
                                                                        ])
                                                                    ])
                                                                ], 4 /* STYLE */)
                                                            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                                            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                                _createCommentVNode(" Technischer Fehler "),
                                                                _createElementVNode("div", _hoisted_26, [
                                                                    _createElementVNode("div", _hoisted_27, [
                                                                        _cache[17] || (_cache[17] = _createElementVNode("i", { class: "fas fa-exclamation-circle text-yellow-600 mt-1 mr-3" }, null, -1 /* CACHED */)),
                                                                        _createElementVNode("div", _hoisted_28, [
                                                                            _cache[16] || (_cache[16] = _createElementVNode("h4", { class: "font-semibold text-yellow-800" }, "Überprüfung nicht möglich", -1 /* CACHED */)),
                                                                            _createElementVNode("p", _hoisted_29, _toDisplayString(addressValidationResult.value.error || 'Die Liefergebiet-Überprüfung ist momentan nicht verfügbar.'), 1 /* TEXT */)
                                                                        ])
                                                                    ])
                                                                ])
                                                            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                                ]))
                                                : _createCommentVNode("v-if", true),
                                            _createCommentVNode(" ✅ COLLAPSIBLE: Zusätzliche Informationen "),
                                            _createElementVNode("div", _hoisted_30, [
                                                _createElementVNode("button", {
                                                    onClick: toggleAdditionalInfo,
                                                    type: "button",
                                                    class: "w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                                                }, [
                                                    _cache[18] || (_cache[18] = _createElementVNode("div", { class: "flex items-center gap-2" }, [
                                                        _createElementVNode("span", { class: "text-sm font-medium text-gray-700" }, " Lieferanweisungen ")
                                                    ], -1 /* CACHED */)),
                                                    _createElementVNode("i", {
                                                        class: _normalizeClass(["transition-transform duration-200 text-gray-400", additionalInfoOpen.value ? 'fas fa-chevron-up' : 'fas fa-chevron-down'])
                                                    }, null, 2 /* CLASS */)
                                                ]),
                                                (additionalInfoOpen.value)
                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
                                                        _withDirectives(_createElementVNode("textarea", {
                                                            ref_key: "additionalInfoTextarea",
                                                            ref: additionalInfoTextarea,
                                                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((additionalInfo).value = $event)),
                                                            name: "delivery-notes",
                                                            autocomplete: "shipping additional-name",
                                                            rows: "3",
                                                            maxlength: "180",
                                                            class: "w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 transition-colors resize-none",
                                                            style: _normalizeStyle({
                                                                '--tw-ring-color': modalInputFocusBorder.value,
                                                                borderColor: undefined
                                                            }),
                                                            placeholder: "z.B. 2. Stock, Klingel rechts, Hinterhof..."
                                                        }, null, 4 /* STYLE */), [
                                                            [_vModelText, additionalInfo.value]
                                                        ]),
                                                        _createElementVNode("div", _hoisted_32, _toDisplayString(additionalInfo.value.length) + "/180 ", 1 /* TEXT */)
                                                    ]))
                                                    : _createCommentVNode("v-if", true)
                                            ])
                                        ], 32 /* NEED_HYDRATION */)
                                    ]),
                                    _createCommentVNode(" Modal Footer "),
                                    _createElementVNode("div", _hoisted_33, [
                                        _createElementVNode("button", {
                                            onClick: closeModal,
                                            class: "flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-semibold"
                                        }, " Abbrechen "),
                                        _createElementVNode("button", {
                                            onClick: saveData,
                                            disabled: !canSave.value || isValidatingAddress.value,
                                            class: _normalizeClass(["modal-primary-btn flex-1 px-4 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 border-2", (!canSave.value || isValidatingAddress.value) ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300' : '']),
                                            style: _normalizeStyle((canSave.value && !isValidatingAddress.value) ? {
                                                backgroundColor: modalButtonPrimary.value,
                                                borderColor: modalButtonPrimary.value,
                                                color: '#FFFFFF'
                                            } : {})
                                        }, [
                                            (isValidatingAddress.value)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_35))
                                                : _createCommentVNode("v-if", true),
                                            _createElementVNode("span", null, _toDisplayString(isValidatingAddress.value ? 'Prüfe Adresse...' : 'Speichern'), 1 /* TEXT */)
                                        ], 14 /* CLASS, STYLE, PROPS */, _hoisted_34)
                                    ])
                                ])
                            ])
                        ]))
                        : _createCommentVNode("v-if", true)
                ]))
            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */));
        };
    }
});
